// feature/bot-litisoft — sprint90. Admin cockpit · Reconciliación. Classifies
// LITISOFT-pending tutela cases against the existing processes/proceso_id
// structure (matched_one / ambiguous_many) and manages the Drive-root registry
// the Phase-2 scan job reads. Admin-only (route + sidebar gated).
import React, { useEffect, useState } from "react";
import { bot } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { useToast } from "../../components/Toast";
import { useCaseLauncher } from "../../components/CaseLauncher";

const STATUS_LABEL = {
  matched_one: "Coincide (1)",
  ambiguous_many: "Ambiguo / acumulado",
  unmatched_none: "Sin coincidencia",
};
const STATUS_COLOR = {
  matched_one: "var(--color-success)",
  ambiguous_many: "var(--color-warning)",
  unmatched_none: "var(--text-hint)",
};
const BLOCK_LABEL = {
  accumulado: "acumulado (varios casos)",
  short_form_or_unlinked: "radicado corto / sin proceso",
  missing_hechos: "faltan hechos",
};

function StatusPill({ status }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: "var(--r-pill)",
      fontSize: 12, fontWeight: 600, color: "#fff",
      background: STATUS_COLOR[status] || "var(--text-hint)",
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function BotLitisoftReconciliacion() {
  const toast = useToast();
  const launcher = useCaseLauncher();
  const [data, setData] = useState(null);  // { items, summary }
  const [roots, setRoots] = useState(null);
  const [label, setLabel] = useState("");
  const [folderId, setFolderId] = useState("");
  const [busy, setBusy] = useState(false);

  function loadRoots() {
    bot.driveRoots().then((r) => setRoots(r.data.drive_roots)).catch(() => setRoots([]));
  }
  function load() {
    setData(null);
    bot.reconciliation().then((r) => setData(r.data)).catch(() => setData({ items: [], summary: {} }));
    loadRoots();
  }
  useEffect(() => { load(); }, []);

  async function addRoot(e) {
    e.preventDefault();
    if (!label.trim() || !folderId.trim()) return;
    setBusy(true);
    try {
      await bot.addDriveRoot({ label: label.trim(), drive_folder_id: folderId.trim() });
      toast.push("Carpeta de Drive registrada");
      setLabel(""); setFolderId(""); loadRoots();
    } catch (err) { toast.push(err?.response?.data?.error || "Error", "error"); }
    finally { setBusy(false); }
  }
  async function toggleRoot(root) {
    try { await bot.toggleDriveRoot(root.id, !root.active); loadRoots(); }
    catch (err) { toast.push(err?.response?.data?.error || "Error", "error"); }
  }
  async function crearScan() {
    const active = (roots || []).filter((r) => r.active).map((r) => r.id);
    if (!active.length) { toast.push("Activa al menos una carpeta de Drive", "error"); return; }
    setBusy(true);
    try {
      const r = await bot.createRun({ kind: "scan", drive_root_ids: active });
      toast.push(`Corrida de reconciliación creada (#${r.data.run_id}). El worker la procesará (Fase 2).`);
    } catch (err) { toast.push(err?.response?.data?.error || "Error", "error"); }
    finally { setBusy(false); }
  }

  const s = (data && data.summary) || {};
  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="muted">
        Aquí clasifico las tutelas pendientes de LITISOFT contra los procesos que ya existen en
        Athena. Sólo dejo pasar las que coinciden con un <strong>único</strong> proceso y tienen
        hechos; los acumulados y los radicados cortos los rechazo automáticamente — nunca radico
        un caso ambiguo por mi cuenta.
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          ["Total pendientes", s.total],
          ["Coinciden (1)", s.matched_one],
          ["Ambiguos / acumulados", s.ambiguous_many],
          ["Elegibles", s.eligible],
          ["Sin hechos", s.blocked_missing_hechos],
        ].map(([k, v]) => (
          <div key={k} className="card" style={{ padding: "10px 16px", minWidth: 130 }}>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{v ?? "—"}</div>
            <div className="muted" style={{ fontSize: 12 }}>{k}</div>
          </div>
        ))}
      </div>

      {/* Reconciliation table */}
      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.items.length ? (
          <Empty title="No hay casos pendientes de LITISOFT" />
        ) : (
          <table>
            <thead>
              <tr><th>Caso</th><th>Radicado</th><th>Estado</th><th>Casos en proceso</th><th>Bloqueos</th></tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id}>
                  <td onClick={() => launcher.open(it.id)} style={{ cursor: "pointer" }}><strong>{it.case_number}</strong></td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{it.radicado}</td>
                  <td><StatusPill status={it.match_status} /></td>
                  <td>{it.proceso_live_cases}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {it.blocked_by.length ? it.blocked_by.map((b) => BLOCK_LABEL[b] || b).join(", ") : (it.eligible ? "—" : "")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drive roots registry */}
      <h2 style={{ margin: "8px 0 0" }}>Carpetas de Drive (raíces para el escaneo)</h2>
      <div className="muted" style={{ marginTop: -8 }}>
        El bot no tiene una raíz fija; el escaneo lee estas carpetas. El worker (Fase 2) confirmará
        el acceso y devolverá la lista de expedientes para reconciliar.
      </div>
      <form style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }} onSubmit={addRoot}>
        <div className="col" style={{ gap: 4 }}>
          <label style={{ fontSize: 12 }}>Etiqueta</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="p. ej. Expedientes 2025" />
        </div>
        <div className="col" style={{ gap: 4 }}>
          <label style={{ fontSize: 12 }}>ID de carpeta de Drive</label>
          <input value={folderId} onChange={(e) => setFolderId(e.target.value)} placeholder="1AbC…" style={{ minWidth: 240 }} />
        </div>
        <button className="secondary" disabled={busy} type="submit">Registrar</button>
        <button disabled={busy} type="button" onClick={crearScan}>Crear corrida de reconciliación</button>
      </form>
      <div className="card" style={{ padding: 0 }}>
        {roots == null ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={18} /></div>
        ) : !roots.length ? (
          <Empty title="Sin carpetas registradas" />
        ) : (
          <table>
            <thead><tr><th>Etiqueta</th><th>ID de carpeta</th><th>Activa</th><th></th></tr></thead>
            <tbody>
              {roots.map((r) => (
                <tr key={r.id}>
                  <td>{r.label}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{r.drive_folder_id}</td>
                  <td>{r.active ? "Sí" : "No"}</td>
                  <td><button className="ghost sm" onClick={() => toggleRoot(r)}>{r.active ? "Desactivar" : "Activar"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
